// albumTemplates.js — print‑ready template catalog for the Album Page Editor
// Drop this in: src/utils/albumTemplates.js
// Used by AlbumPageEditor + albumLayouts.editor helpers

// Default page template when nothing is specified
export const DEFAULT_TEMPLATE_KEY = 'grid4';

// Core catalog — align names with UI copy
// kind: 'cover' | 'chapter' | 'map' | 'stats' | 'hero' | 'grid' | 'timeline' | 'journal'
// orientation is a hint for preview; your PDF/export can map this to sizes
export const TEMPLATES = {
  cover: {
    key: 'cover',
    kind: 'cover',
    orientation: 'landscape',
    photosPerPage: 1,
    hints: { title: true, subtitle: true },
  },
  chapter: {
    key: 'chapter',
    kind: 'chapter',
    orientation: 'landscape',
    photosPerPage: 1,
    hints: { title: true },
  },
  mapSpread: {
    key: 'mapSpread',
    kind: 'map',
    orientation: 'landscape',
    photosPerPage: 0,
    hints: { map: true }, // render route raster inside exporter
  },
  stats: {
    key: 'stats',
    kind: 'stats',
    orientation: 'landscape',
    photosPerPage: 0,
    hints: { stats: true },
  },
  hero: {
    key: 'hero',
    kind: 'hero',
    orientation: 'landscape',
    photosPerPage: 5, // 1 large + 4 support
    grid: { cols: 4, rows: 3, heroSpan: { cols: 2, rows: 2 } },
  },
  grid4: {
    key: 'grid4',
    kind: 'grid',
    orientation: 'landscape',
    photosPerPage: 4,
    grid: { cols: 2, rows: 2 },
  },
  grid6: {
    key: 'grid6',
    kind: 'grid',
    orientation: 'landscape',
    photosPerPage: 6,
    grid: { cols: 3, rows: 2 },
  },
  timeline: {
    key: 'timeline',
    kind: 'timeline',
    orientation: 'landscape',
    photosPerPage: 3, // left image + right caption block
    hints: { captions: true },
  },
  journal: {
    key: 'journal',
    kind: 'journal',
    orientation: 'landscape',
    photosPerPage: 1, // large text with small image
    hints: { text: true, smallPhoto: true },
  },
};

// Utility helpers
export function templateKeys() { return Object.keys(TEMPLATES); }
export function templateFor(key) { return TEMPLATES[key] || TEMPLATES[DEFAULT_TEMPLATE_KEY]; }
export function photosPerPageFor(key) { return templateFor(key).photosPerPage || 4; }
export function isValidTemplateKey(key) { return !!TEMPLATES[key]; }

// Recommend a sensible initial page count for a given photo set and template
export function recommendedInitialPages(photoCount = 0, templateKey = DEFAULT_TEMPLATE_KEY) {
  const per = photosPerPageFor(templateKey);
  const pages = Math.ceil(Math.max(1, Number(photoCount) || 0) / per);
  return Math.max(1, pages);
}

// Optional size hints you can use in your exporter (A4 landscape & 21x21 square)
export const BOOK_SIZES = {
  'A4-landscape': { mm: { w: 297, h: 210 }, cssAspect: 297 / 210 },
  'Square-21': { mm: { w: 210, h: 210 }, cssAspect: 1 },
};

export function aspectFor(bookOptions) {
  const key = bookOptions?.size || 'A4-landscape';
  return (BOOK_SIZES[key] || BOOK_SIZES['A4-landscape']).cssAspect;
}

// Aliases so builder's page keys resolve without changes:
TEMPLATES['stats-left'] = {
  key: 'stats-left',
  kind: 'stats',
  orientation: 'landscape',
  photosPerPage: 0,
  hints: { stats: true },
  side: 'left' // optional hint; renderer can ignore
};

TEMPLATES['route-right'] = {
  key: 'route-right',
  kind: 'map',
  orientation: 'landscape',
  photosPerPage: 0,
  hints: { map: true },
  side: 'right' // optional hint
};

// Aliases used by builder/wizard
TEMPLATES['stats-left']  = { ...TEMPLATES.stats,      key: 'stats-left'  };
TEMPLATES['route-right'] = { ...TEMPLATES.mapSpread,  key: 'route-right' };